import React, { useEffect, useRef, useState } from "react";
import { Modal, Table } from "react-bootstrap";
import { toast } from "./AppToast";
import { resizeSerialRows, toSerialQty } from "../utils/serialCapture";

/**
 * One row per unit of `qty`; columns come from `buildSerialColumns`.
 * Required columns (Serial No, IMEI No) show * and must be filled before Add Record.
 * Optional columns (Other*) show only when configured on the SKU — no asterisk, not validated.
 * Enter moves to the next input so barcode scanners can fill rows continuously.
 */
export default function SerialCaptureModal({
  show,
  onHide,
  onSave,
  qty,
  columns = [],
  initialRows = [],
  title = "Serial / IMEI Details",
  subtitle = "",
  readOnly = false,
}) {
  const [rows, setRows] = useState([]);
  const [invalid, setInvalid] = useState({});
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setRows(resizeSerialRows(initialRows, qty));
    setInvalid({});
    // Seed only when the modal opens so parent re-renders don't wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const cellId = (r, c) => `serial-cell-${r}-${c}`;

  const handleChange = (rowIdx, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, [key]: value } : r)));
    setInvalid((prev) => {
      const id = `${rowIdx}:${key}`;
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const focusNext = (rowIdx, colIdx) => {
    const nextCol = colIdx + 1 < columns.length ? colIdx + 1 : 0;
    const nextRow = nextCol === 0 ? rowIdx + 1 : rowIdx;
    const el = bodyRef.current?.querySelector(`#${cellId(nextRow, nextCol)}`);
    if (el) el.focus();
  };

  const requiredColumns = columns.filter((c) => c.required);

  const validate = () => {
    const errs = {};
    requiredColumns.forEach((col) => {
      const seen = new Map();
      rows.forEach((r, i) => {
        const v = String(r?.[col.key] ?? "").trim();
        if (!v) {
          errs[`${i}:${col.key}`] = `${col.label} is required`;
          return;
        }
        const k = v.toLowerCase();
        if (seen.has(k)) {
          errs[`${i}:${col.key}`] = `Duplicate ${col.label}`;
          errs[`${seen.get(k)}:${col.key}`] = `Duplicate ${col.label}`;
        } else {
          seen.set(k, i);
        }
      });
    });
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setInvalid(errs);
      const missing = requiredColumns
        .filter((col) => rows.some((r) => !String(r?.[col.key] ?? "").trim()))
        .map((c) => c.label);
      const msg =
        missing.length > 0
          ? `Please fill mandatory fields: ${missing.join(", ")}`
          : Object.values(errs)[0];
      toast.error(msg);
      return;
    }
    const trimmed = rows.map((r) =>
      Object.fromEntries(columns.map((c) => [c.key, String(r?.[c.key] ?? "").trim()]))
    );
    onSave && onSave(trimmed);
  };

  const total = toSerialQty(qty);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          {title}
          {subtitle ? <span className="text-muted ms-2 small">{subtitle}</span> : null}
          <span className="badge bg-secondary ms-2">Qty: {total}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body ref={bodyRef} style={{ maxHeight: "60vh" }}>
        {total === 0 ? (
          <div className="text-muted text-center py-3">Enter Order Qty first.</div>
        ) : (
          <Table bordered size="sm" className="mb-0 align-middle">
            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ width: 60 }}>#</th>
                {columns.map((c) => (
                  <th key={c.key}>
                    {c.label}
                    {c.required && !readOnly ? <span className="text-danger"> *</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="text-muted">{rowIdx + 1}</td>
                  {columns.map((col, colIdx) => {
                    const err = invalid[`${rowIdx}:${col.key}`];
                    return (
                      <td key={col.key}>
                        <input
                          id={cellId(rowIdx, colIdx)}
                          type="text"
                          autoComplete="off"
                          className={`form-control form-control-sm ${err ? "is-invalid" : ""}`}
                          value={row?.[col.key] ?? ""}
                          title={err || undefined}
                          placeholder={col.label}
                          disabled={readOnly}
                          onChange={(e) => handleChange(rowIdx, col.key, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusNext(rowIdx, colIdx);
                            }
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onHide}>
          {readOnly ? "Close" : "Cancel"}
        </button>
        {!readOnly && total > 0 && (
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            Add Record
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export function SerialCaptureButton({ captured = 0, qty = 0, onClick, disabled, className = "" }) {
  const total = toSerialQty(qty);
  const done = total > 0 && captured >= total;
  return (
    <button
      type="button"
      className={`btn ${done ? "btn-outline-success" : "btn-outline-primary"} ${className}`}
      onClick={onClick}
      disabled={disabled || total === 0}
      title={total === 0 ? "Enter quantity first" : "Capture Serial / IMEI"}
      style={{ whiteSpace: "nowrap" }}
    >
      <i className="bi bi-upc-scan me-1" />
      {captured}/{total}
    </button>
  );
}
